import * as Color from "color"
import PptxGenJS, {
	Slide,
	ChartPieDataset,
	ChartOptions,
	ChartBarDataset,
	ChartMultiTypeItem,
	ChartMultiTypeItemOptions,
	ChartMultiTypeOptions
} from "pptxgenjs"
import * as React from "react"
import * as ReactDOM from "react-dom"
import { IntlProvider } from "react-intl"
import OperationTable from "./operation-table"
import OperationProgressTable from "./operation-progress-table"
import * as OperationsUtils from "./utils"
import { ReportConfiguration } from "../../../configuration"
import { getChartBaseOptions,getChartColors } from "../../pptx-configuration/charts"
import * as Constants from "../../pptx-configuration/constants"
import { MasterSlide } from "../../pptx-configuration/master-slides"
import {
	getPlaceholderChartBodyOptions,
	Placeholder
} from "../../pptx-configuration/placeholders"
import { getHtmlToPowerPointBaseOptions } from "../../pptx-configuration/slides"
import { DocumentSection } from "../../types"
import { formatTitle, createDemographyData } from "../../utils"
import * as ChapterDomain from "../../../../../domain/sfp/chapter-domain"
import * as Model from "../../../../../model"

export function checkOperationSection(
	configuration: ReportConfiguration,
	receipts: boolean
): boolean {
	const budgetKey = receipts ? "receipts" : "expense"
	const charts = configuration.observation.operationSection[budgetKey].charts
	return configuration.observation.operationSection[budgetKey].tables
	|| charts.global
	|| !!(charts.chapters && charts.chapters.length)
}

export function checkProgressOperationSection(
	configuration: ReportConfiguration,
	receipts: boolean
): boolean {
	const budgetKey = receipts ? "receipts" : "expense"
	return configuration.observation.operationSection[budgetKey].blankTable
}


export function createOperationBudgetSection(
	pptx: PptxGenJS,
	configuration: ReportConfiguration,
	numbering: number[],
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	years: number[],
	receipts: boolean,
	isGabaritDob:boolean
): DocumentSection {
	const budgetKey = receipts ? "receipts" : "expense"
	const budgetLabel = receipts ? "Recettes" : "Dépenses"
	const title = formatTitle(`${budgetLabel} de fonctionnement`, numbering)
	const slides: Slide[] = []
	const referenceYear = configuration.generalities.referenceYear
	const operationsData = OperationsUtils.createData(
		localAuthority,
		scenario,
		ppi,
		loanDecompositions,
		receipts,
		years
	)
	const placeholderBodyOptions = getPlaceholderChartBodyOptions()
	const baseColor = new Color(configuration.table.header.backgroundColor)
	const gabaritDob = isGabaritDob;
	// tables
	if (configuration.observation.operationSection[budgetKey].tables && !configuration.observation.operationSection[budgetKey].charts) {
		createTable(configuration,budgetKey,operationsData,years,title,pptx);
	}

	// charts
	const charts = configuration.observation.operationSection[budgetKey].charts

	if (charts) {
		// global charts
		if (charts.global) {
			const slide = pptx.addNewSlide(MasterSlide.Chart)
			slide.addText(title, {
				placeholder: Placeholder.Title
			})

			const labels = operationsData.realOperations.rows
			.map(row => row.chapter ? row.chapter.label : "Non chapitré")

			const values = operationsData.realOperations.rows
			.map(row => row.values[referenceYear])

			const data: [ChartPieDataset] = [
				{ labels, name: `${budgetLabel} réelles de fonctionnement`, values }
			]

			const options: ChartOptions = {
				...getChartBaseOptions(placeholderBodyOptions),
				title: `${budgetLabel} réelles de l'année ${referenceYear}`,
				showPercent:true,
				dataLabelFontBold:true,
				dataLabelColor: "000000",
				chartColors:getChartColors(),
				legendFontSize:11,
			}

			slide.addChart(pptx.charts.PIE, data, options)

			slides.push(slide)
		}

		// chapters charts
		if (charts.chapters && charts.chapters.length) {
			// Récupération des chapitres réels parmi ceux sélectionnés
			const realChapters = ChapterDomain.filterRealChapters(charts.chapters)

			// Si la liste des chapitres réels est vide alors il n'est pas nécessaire
			// de créer les slides et graphiques
			if (realChapters.length) {
				/** Données du graphique global des chapitres sélectionnés */
				const globalChaptersData = OperationsUtils.createGlobalChaptersData(
					operationsData, years)

				// global chapters chart
				const globalChaptersSlide = pptx.addNewSlide(MasterSlide.Chart)
				globalChaptersSlide.addText(title, { placeholder: Placeholder.Title })

				const globalChaptersCaption = `${budgetLabel} réelles de fonctionnement`
				const options: ChartOptions = {
					...getChartBaseOptions(placeholderBodyOptions),
					title: globalChaptersCaption,
					chartColors: getChartColors(),
				}

				globalChaptersSlide.addChart(pptx.charts.BAR, globalChaptersData, options)

				slides.push(globalChaptersSlide)

				// individual chapter charts
				if (charts.onePerChapter) {
					globalChaptersData.forEach((chapterData, i) => {
						const caption = `${budgetLabel} réelles de fonctionnement par `
						+ `chapitre : ${chapterData.name}`
						const perChapterData: [ChartBarDataset] = [{
							labels: chapterData.labels.slice(),
							name: chapterData.name,
							values: chapterData.values.slice()
						}]

						// Si les données du chapitre ne contiennent aucune ligne, passer à
						// l'itération suivante pour prévenir la création de slides vides.
						if (!perChapterData[0].values.length) return

						const slide = pptx.addNewSlide(MasterSlide.Chart)
						slide.addText(title, { placeholder: Placeholder.Title })

						const options: ChartOptions = {
							...getChartBaseOptions(placeholderBodyOptions),
							showLegend: false,
							showValue:true,
							dataLabelColor: "000000",
							dataLabelFontSize: 11,
							dataLabelFontBold:true,
							title: caption,
							chartColors:[baseColor.hex().substr(1)],
							legendFontSize:11,
						}

						slide.addChart(pptx.charts.BAR, perChapterData, options)

						slides.push(slide)
					})
				}
				const globalPerInhabitantData = OperationsUtils
				.createGlobalPerInhabitantData(
					globalChaptersData,
					localAuthority.demographies,
					years
				)

				if(!gabaritDob){

					const globalPerInhabitantSlide = pptx.addNewSlide(MasterSlide.Chart)
					globalPerInhabitantSlide.addText(title, {
						placeholder: Placeholder.Title
					})

					const globalPerInhabitantCaption = `${budgetLabel} réelles de `
					+ `fonctionnement par habitant`
					const globalPerInhabitantOptions: ChartMultiTypeOptions = {
						...getChartBaseOptions(placeholderBodyOptions, null),
						title: globalPerInhabitantCaption,
						catAxes: [{}, { catAxisHidden: true }],
						valAxes: [
							{ valAxisLabelFormatCode: Constants.STRING_FORMAT_CURRENCY },
							{
								valAxisLabelFormatCode: "#,##0",
								valAxisLineShow: true,
								valGridLine: "none"
							}
						],
						chartColors: getChartColors(),
						legendFontSize:11,
					} as ChartMultiTypeOptions

					const populationData = createDemographyData(
						localAuthority.demographies,
						years
					)

					const populationChartOptions: ChartMultiTypeItemOptions = {
						chartColors: ["666666"],
						chartColorsOpacity: 50,
						legendFontSize:11,
						lineSize: 1,
						secondaryCatAxis: true,
						secondaryValAxis: true
					}

					const globalPerInhabitantCharts: ChartMultiTypeItem[] = [
						{ data: globalPerInhabitantData, type: pptx.charts.BAR },
						{
							data: populationData,
							options: populationChartOptions,
							type: pptx.charts.LINE
						}
					]
				
					globalPerInhabitantSlide.addChart(
						globalPerInhabitantCharts,
						globalPerInhabitantOptions
					)
					
					slides.push(globalPerInhabitantSlide)
				}

				// per inhabitant chapter charts
				if (charts.onePerChapterPerInhabitant) {
					globalPerInhabitantData.forEach((perInhabitantData, i) => {
						const perChapterPerInhabitantCaption = `${budgetLabel} réelles de `
						+ `fonctionnement par chapitre et par habitant : `
						+ perInhabitantData.name
						const perChapterPerInhabitantData: [ChartBarDataset] = [{
							labels: perInhabitantData.labels.slice(),
							name: perInhabitantData.name,
							values: perInhabitantData.values.slice()
						}]

						// Si les données du chapitre ne contiennent aucune ligne, passer à
						// l'itération suivante pour prévenir la création de slides vides.
						if (!perChapterPerInhabitantData[0].values.length) return

						const perChapterPerInhabitantSlide = pptx.addNewSlide(MasterSlide.Chart)
						perChapterPerInhabitantSlide.addText(title, {
							placeholder: Placeholder.Title
						})

						const options: ChartOptions = {
							...getChartBaseOptions(placeholderBodyOptions),
							showLegend: false,
							title: perChapterPerInhabitantCaption,							
							showValue: true,
							chartColors:[baseColor.hex().substr(1)],
							legendFontSize:11
						}

						perChapterPerInhabitantSlide.addChart(
							pptx.charts.BAR,
							perChapterPerInhabitantData,
							options
						)

						slides.push(perChapterPerInhabitantSlide)
					})
				}
			}
		}

		if (configuration.observation.operationSection[budgetKey].tables) {
			createTable(configuration,budgetKey,operationsData,years,title,pptx);
		}
	}

	return { slides, title }
}

function createTable(configuration:ReportConfiguration,budgetKey,operationsData,years,title,pptx){
	const tempDiv = document.createElement("div")
	document.head.appendChild(tempDiv)

	const tableId = `management-report-operation-${budgetKey}`
	const table = <IntlProvider locale={navigator.language} defaultLocale="fr">
		<OperationTable
		currency={configuration.currency}
		data={operationsData}
		id={tableId}
		locale={configuration.locale}
		roundness={configuration.generalities.roundness}
		tableConfiguration={configuration.table}
		years={years} />
	</IntlProvider>

	ReactDOM.render(table, tempDiv, () => {
		let options = getHtmlToPowerPointBaseOptions(title)
		pptx.addSlidesForTable(tableId, options)
		document.head.removeChild(tempDiv)
	})
}


export function createProgressOperationBudgetSection(
	pptx: PptxGenJS,
	configuration: ReportConfiguration,
	numbering: number[],
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	years: number[],
	receipts: boolean
): DocumentSection {
	const budgetKey = receipts ? "receipts" : "expense"
	const budgetLabel = receipts ? "Progression des recettes" : "Progression des dépenses"
	const title = formatTitle(`${budgetLabel}`, numbering)
	const slides: Slide[] = []
	const operationsData = OperationsUtils.createProgressData(
		localAuthority,
		scenario,
		ppi,
		loanDecompositions,
		receipts,
		years,
		configuration.generalities.referenceYear
	)

	// tables
	if (configuration.observation.operationSection[budgetKey].blankTable) {
		createProgressTable(configuration,budgetKey,operationsData,years,title,pptx,receipts);
	}
	return { slides, title }
}


function createProgressTable(configuration:ReportConfiguration,budgetKey,operationsData,years,title,pptx,receipts){
	const tempDiv = document.createElement("div")
	document.head.appendChild(tempDiv)

	const tableId = `management-report-operation-${budgetKey}`
	const table = <IntlProvider locale={navigator.language} defaultLocale="fr">
		<OperationProgressTable
		currency={configuration.currency}
		data={operationsData}
		id={tableId}
		locale={configuration.locale}
		roundness={configuration.generalities.roundness}
		tableConfiguration={configuration.table}
		years={years} 
		referenceYear={configuration.generalities.referenceYear}
		receipts={receipts}/>
	</IntlProvider>

	ReactDOM.render(table, tempDiv, () => {
		let options = getHtmlToPowerPointBaseOptions(title)
		pptx.addSlidesForTable(tableId, options)
		document.head.removeChild(tempDiv)
	})
}




// WEBPACK FOOTER //
// ./src/component/management-report/generator/observation-generator/operation-generator/index.tsx